import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AuthContextState, AuthUser } from "./authProvider";

type SessionStatus =
  | "initializing"
  | "authenticated"
  | "signingOut"
  | "unauthenticated";

interface CognitoSessionState {
  user?: AuthUser;
  status: SessionStatus;
}

export const useCognitoAuthState = () => {
  const { user, status, login, logout } = useCognitoAuthUser();

  return useMemo<AuthContextState>(
    () => ({
      user,
      isLoading: status === "initializing",
      login,
      logout,
    }),
    [user, status, login, logout],
  );
};

export const useCognitoAuthUser = () => {
  const [state, setState] = useState<CognitoSessionState>({
    status: "initializing",
  });

  const clearSession = useCallback(() => {
    setState({ status: "unauthenticated" });
  }, []);

  const signingOut = useRef(false);
  const isSigningOut = useCallback(() => signingOut.current, []);

  const login = useCallback(async () => {
    if (isSigningOut()) return;
    await signInWithRedirect();
  }, [isSigningOut]);

  const loadSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (signingOut.current) {
        return undefined;
      }
      if (!session?.userSub || !session.tokens) {
        setState({ status: "unauthenticated" });
        return undefined;
      }

      signingOut.current = false;
      setState({
        user: {
          sub: session.userSub,
          email: (session.tokens.idToken?.payload.email as string) ?? "",
        },
        status: "authenticated",
      });
    } catch (error) {
      setState({ status: "unauthenticated" });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    signingOut.current = true;
    setState({ status: "signingOut" });

    try {
      await signOut();
    } catch (error) {
      signingOut.current = false;
      try {
        await loadSession();
      } catch {
        // Preserve the original sign-out error after attempting to restore state.
      }
      throw error;
    }
  }, [loadSession]);

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        await loadSession();
      } catch (error) {
        console.error("Error fetching user session:", error);
      }
    };

    void getCurrentUser();

    const hubListener = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          signingOut.current = false;
          void getCurrentUser();
          break;
        case "tokenRefresh":
          if (!signingOut.current) {
            void getCurrentUser();
          }
          break;
        case "tokenRefresh_failure":
          clearSession();
          break;
        case "signedOut":
          signingOut.current = false;
          clearSession();
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, [clearSession, loadSession]);

  return {
    ...state,
    isLoading: state.status === "initializing",
    login,
    logout,
  };
};
